// @flow

import React from 'react';
import {renderers} from 'markdoc';
import Box from 'sail/Box';
import Badge from 'sail/Badge';
import {UnstyledLink} from 'sail/UnstyledLink';
import * as components from 'docs/components';
import {useAccount} from 'docs/data';
import {Callout} from 'docs/components';
import type {Article, ArticlePublicMetadata} from 'docs/data';
import {defineMessages, FormattedMessage} from 'react-intl';
import classnames from 'classnames';
import {
  RequirementsBanner,
  InvalidRequirementsBanner,
} from './RequirementsBanner';
import {ValidationErrors} from './ValidationErrors';
import {Title} from './Title';
import {Subtitle} from './Subtitle';
import {Footer} from './Footer';

const messages = defineMessages({
  beta: {
    id: 'docsShell.markdocContent.beta',
    description: 'Beta label',
    defaultMessage: 'Beta',
  },
  inviteOnly: {
    id: 'docsShell.markdocContent.inviteOnly',
    description: 'Invite-only label',
    defaultMessage: 'Invite-only',
  },
  draft: {
    id: 'docsShell.markdocContent.draft',
    description: 'Draft label',
    defaultMessage: 'Draft',
  },
  untranslatedNotice: {
    id: 'docsShell.untranslatedNotice',
    description:
      'A notice to show to indicate that content is not yet translated',
    defaultMessage:
      "This page is not yet available in this language. We're working hard to make our documentation available in more languages and will provide a translation as soon as it's available.",
  },
});

const AVAILABILITY_LABELS = {
  beta: <FormattedMessage {...messages.beta} />,
  invite: <FormattedMessage {...messages.inviteOnly} />,
};

type TitleBadgeProps = {
  metadata: ArticlePublicMetadata,
};

const TitleBadge = ({metadata}: TitleBadgeProps) => {
  const {availability, badge} = metadata;

  if (badge) {
    if (typeof badge === 'string') {
      return <Badge color="green" label={badge} />;
    } else {
      const badgeContent = (
        // $FlowExpectedError color wants a specific string but we just have a generic one
        <Badge color={badge.color || 'green'} label={badge.text} />
      );

      return badge.link ? (
        <UnstyledLink href={badge.link}>{badgeContent}</UnstyledLink>
      ) : (
        badgeContent
      );
    }
  }

  if (availability && availability !== 'public') {
    return <Badge color="green" label={AVAILABILITY_LABELS[availability]} />;
  }

  return null;
};

export type MarkdocContentProps = {|
  article: Article,
|};

export const MarkdocContent = ({article}: MarkdocContentProps) => {
  const render = renderers.react(article.content, React);
  const {account} = useAccount();
  const {features, metadata, xray} = article;

  const untranslatedNotice =
    !article.intl.translated && !article.intl.locale.startsWith('en') ? (
      <Callout type="note">
        <p>
          <FormattedMessage {...messages.untranslatedNotice} />
        </p>
      </Callout>
    ) : null;

  const className = classnames('Content-article', 'MarkdocContentWrapper', {
    'Content-LandingPage': article.metadata.type === 'landing-page',
  });

  return (
    <Box className={className}>
      {untranslatedNotice}
      <article id="content" data-locale={account.locale.code}>
        {xray?.conditions && (
          <>
            <InvalidRequirementsBanner conditions={xray.conditions} />
            <RequirementsBanner
              conditions={xray.conditions}
              internal={xray.internal}
              allowed={xray.allowed}
            />
          </>
        )}
        {!!article.errorNodes?.length && account.xray && (
          <ValidationErrors errors={article.errorNodes} />
        )}
        <Box padding={{bottom: 24}}>
          <Title>
            {article.metadata.title}
            {metadata.published === false && (
              <Badge
                color="yellow"
                label={<FormattedMessage {...messages.draft} />}
              />
            )}
            <TitleBadge metadata={metadata} />
          </Title>
          {article.metadata.subtitle && (
            <Subtitle>{article.metadata.subtitle}</Subtitle>
          )}
        </Box>
        {render({components})}
      </article>
      {features.footer && <Footer article={article} />}
    </Box>
  );
};
